/**
 * Where addresses from before the redesign land (emails, notifications, the phone app and bookmarks still use them;
 * pages.html "Route map"). Explore and the old mission list become Robot › Tasks with their search kept; a recording
 * or scanning link lands on that kind of work's own place; Activity's robot rows became Robot › My runs.
 */
import type { Contribution, StatusKey } from '@robotensor/api'
import type { Quick } from '../lib/robot.ts'
import { captureOfRef, submissionOfRef } from '../lib/robot.ts'
import { clean } from '../lib/search.ts'

export interface RobotSearch {
  q?: string
  f?: Quick
  area?: string
  task?: string
  y?: number
}

export type OldTarget = { to: '/robot'; search: RobotSearch } | { to: '/recordings' } | { to: '/account/work' }

export function oldBrowseTarget(s: { q?: string; path?: string; kind?: string; bookmarked?: boolean }): OldTarget {
  if (s.path === 'ego' || s.kind === 'record') return { to: '/recordings' }
  if ((s.path && s.path !== 'simulation') || s.kind === 'scan') return { to: '/account/work' }
  return { to: '/robot', search: clean({ q: s.q, f: s.bookmarked ? ('bookmarked' as const) : undefined }) }
}

/** Activity's status filter (`?status=accepted`) as a My runs filter. */
export function oldStatus(s: string | undefined): StatusKey | undefined {
  if (!s) return undefined
  if (['submitted', 'processing', 'needs_review', 'checking'].includes(s)) return 'checking'
  if (s === 'accepted') return 'accepted'
  if (s === 'needs_correction') return 'fix'
  if (s === 'rejected') return 'rejected'
  return undefined
}

/** Where a contribution's result lives now: a robot run's result page, a recording, or My runs. */
export function resultHref(c: Pick<Contribution, 'domain' | 'ref'> | null | undefined): string {
  if (!c) return '/robot/runs'
  const sub = submissionOfRef(c.ref)
  if (c.domain === 'sim' && sub) return `/robot/runs/${encodeURIComponent(sub)}`
  if (c.domain === 'realdata') {
    const cap = captureOfRef(c.ref)
    return cap ? `/recordings/${encodeURIComponent(cap)}` : '/recordings'
  }
  return '/robot/runs'
}

/** The Recordings tab's status filter (`fix`, `phone`, `review`, `accepted`, `rejected`); older links said
 * `needs_correction`. (Recordings is its own build stream; the filter stays here so the route keeps its shape.) */
export const RECORDING_FILTERS = ['fix', 'phone', 'review', 'accepted', 'rejected'] as const
export type RecordingFilter = (typeof RECORDING_FILTERS)[number]

export function recordingFilter(v: unknown): RecordingFilter | undefined {
  if (v === 'needs_correction') return 'fix'
  return typeof v === 'string' && (RECORDING_FILTERS as readonly string[]).includes(v) ? (v as RecordingFilter) : undefined
}
